import { slice } from 'lodash-es';
import type { ReactElement, ReactNode } from 'react';
import { useState, useRef } from 'react';
import { Icon } from 'src/common/components/icon';
import { VirtuosoList } from 'src/common/components/virtuoso-list';
import type { VirtuosoHandle } from 'react-virtuoso';

// #region 成员列表折叠组件
interface FoldItemListProps<T> {
  items: T[];
  renderItemContent: (props: T) => ReactNode;
  foldLength?: number;
  moreContent?: string;
}
export const FoldItemList = <T extends {}>(props: FoldItemListProps<T>): ReactElement => {
  const { items, renderItemContent, foldLength = 50 } = props;
  const [isFold, setIsFold] = useState(false);
  const virtuosoRef = useRef<VirtuosoHandle>(null);

  // 当展示的数据量较少时，不使用虚拟列表
  const shouldUseVirtualList = items.length > 50;

  const handleToggleFold = (fold: boolean) => {
    setIsFold(fold);
    // 当切换折叠状态时，滚动到顶部
    if (virtuosoRef.current) {
      virtuosoRef.current.scrollToIndex({ index: 0 });
    }
  };

  // 根据当前折叠状态返回需要显示的项目
  const displayItems = !isFold && items.length > foldLength ? slice(items, 0, foldLength) : items;

  const renderItem = (item: T, index: number) => {
    return <>{renderItemContent(item)}</>;
  };

  const computeItemKey = (index: number) => {
    return `member-item-${index}`;
  };

  // 当数据量较小时不使用虚拟列表
  if (!shouldUseVirtualList) {
    if (items.length > foldLength && !isFold) {
      return (
        <>
          {slice(items, 0, foldLength).map((item, index) => (
            <div key={`member-item-${index}`}>{renderItemContent(item)}</div>
          ))}
          <div
            className="text-grey4 text-t3 flex items-center h-9 cursor-pointer"
            onClick={() => handleToggleFold(true)}
          >
            <Icon name="IcArrowShow" size="middle" className="mr-2" />
            {['查看其他', items.length - foldLength, '位', props.moreContent ?? '成员'].join(' ')}
          </div>
        </>
      );
    }
    return (
      <>
        {items.map((item, index) => (
          <div key={`member-item-${index}`}>{renderItemContent(item)}</div>
        ))}
        {items.length > foldLength && isFold && (
          <div
            className="text-grey4 text-t3 flex items-center h-9 cursor-pointer"
            onClick={() => handleToggleFold(false)}
          >
            <Icon name="IcArrowShow" size="middle" className="mr-2 rotate-180" />
            收起
          </div>
        )}
      </>
    );
  }

  // 使用虚拟列表
  return (
    <>
      <VirtuosoList
        items={displayItems}
        renderItem={renderItem}
        computeItemKey={computeItemKey}
        virtuosoRef={virtuosoRef}
        maxHeight="60vh"
      />
      {items.length > foldLength && (
        <div
          className="text-grey4 text-t3 flex items-center h-9 cursor-pointer"
          onClick={() => handleToggleFold(!isFold)}
        >
          <Icon name="IcArrowShow" size="middle" className={`mr-2 ${isFold ? 'rotate-180' : ''}`} />
          {isFold
            ? '收起'
            : ['查看其他', items.length - foldLength, '位', props.moreContent ?? '成员'].join(' ')}
        </div>
      )}
    </>
  );
};
